/**
 * placeholderArt — what an image slot shows before it has an image.
 *
 * Not stock photography (someone else's picture, someone else's licence, and
 * the same picture as every other template) but a soft mesh gradient drawn
 * from the site's OWN Brand colours: a light two-tone wash with a few blurred
 * colour fields floating over it. It reads as an intentional visual, it is
 * unique to the site (change the brand, the art follows), and every slot on a
 * page differs because the composition is seeded by the widget id.
 *
 * Pure and deterministic: (colours, seed) → the same SVG, on the client, in
 * the SSR renderer and in tests. Emitted as a `data:` URI so it needs no
 * asset pipeline and publishes inline.
 */
/// <reference path="./culori.d.ts" />
import { converter, formatHex, parse } from "culori";

const toOklch = converter("oklch");

export interface PlaceholderPalette {
  /** The brand's primary (solid) colour — any CSS colour string. */
  readonly primary: string;
  /** The brand's accent colour. */
  readonly accent: string;
}

/** Unthemed fallback (a canvas with no site theme yet): a quiet blue-grey. */
export const DEFAULT_PLACEHOLDER_PALETTE: PlaceholderPalette = {
  primary: "#64748b",
  accent: "#94a3b8",
};

export interface PlaceholderArtOptions {
  /** Drawing surface; the SVG covers any frame (`xMidYMid slice`). */
  readonly width?: number;
  readonly height?: number;
}

// ── Deterministic randomness ────────────────────────────────────────

/** FNV-1a 32-bit — a stable seed from any string, identical everywhere. */
function hash32(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** mulberry32 — small, fast, good enough for layout jitter. */
function prng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ── Colour derivation (OKLCH so tints stay perceptually even) ───────

interface Lch {
  l: number;
  c: number;
  h: number;
}

function lchOf(color: string, fallback: string): Lch {
  const c = toOklch(parse(color) ?? parse(fallback));
  return { l: c?.l ?? 0.6, c: c?.c ?? 0.05, h: c?.h ?? 250 };
}

/** A colour at a chosen lightness/chroma on the input's hue. Chroma is
 *  clamped so a near-grey brand doesn't turn neon. */
function tone(base: Lch, l: number, c: number, hueShift = 0): string {
  const hex = formatHex({
    mode: "oklch",
    l: Math.max(0, Math.min(1, l)),
    c: Math.max(0, Math.min(0.2, c)),
    h: (base.h + hueShift + 360) % 360,
  });
  return hex ?? "#cccccc";
}

// ── The picture ─────────────────────────────────────────────────────

const fmt = (n: number): string => String(Math.round(n * 10) / 10);

/**
 * Build the SVG markup (not yet URI-encoded). Exported for tests and for
 * hosts that want to inline the element instead of an `<img>`.
 */
export function placeholderArtSvg(
  palette: PlaceholderPalette,
  seed: string,
  options: PlaceholderArtOptions = {},
): string {
  const W = options.width ?? 800;
  const H = options.height ?? 600;
  const rnd = prng(hash32(seed));
  const p = lchOf(palette.primary, DEFAULT_PLACEHOLDER_PALETTE.primary);
  const a = lchOf(palette.accent, DEFAULT_PLACEHOLDER_PALETTE.accent);

  // Wash: a very light tint of each colour, the gradient angle per seed.
  const washFrom = tone(p, 0.94, Math.min(p.c, 0.05));
  const washTo = tone(a, 0.93, Math.min(a.c, 0.06));
  const angle = rnd() * 360;
  const rad = (angle * Math.PI) / 180;
  const x1 = fmt(50 - Math.cos(rad) * 50);
  const y1 = fmt(50 - Math.sin(rad) * 50);
  const x2 = fmt(50 + Math.cos(rad) * 50);
  const y2 = fmt(50 + Math.sin(rad) * 50);

  // Fields: 4 blurred ellipses alternating primary / accent, mid-light so
  // they read as colour without fighting foreground text laid over them.
  const fields = [
    tone(p, 0.74, p.c + 0.03),
    tone(a, 0.82, a.c * 0.8 + 0.02),
    tone(p, 0.82, p.c * 0.8 + 0.02, 18),
    tone(a, 0.78, a.c * 0.9 + 0.02, -14),
  ];
  const ellipses = fields
    .map((fill, i) => {
      // Spread the fields across the frame (one per quadrant, jittered) so
      // no seed collapses them into a single blob.
      const qx = i % 2 === 0 ? 0.28 : 0.72;
      const qy = i < 2 ? 0.3 : 0.7;
      const cx = (qx + (rnd() - 0.5) * 0.35) * W;
      const cy = (qy + (rnd() - 0.5) * 0.35) * H;
      const rx = (0.22 + rnd() * 0.16) * W;
      const ry = (0.2 + rnd() * 0.18) * H;
      const rot = fmt((rnd() - 0.5) * 60);
      return (
        `<ellipse cx="${fmt(cx)}" cy="${fmt(cy)}" rx="${fmt(rx)}" ry="${fmt(ry)}"` +
        ` fill="${fill}" fill-opacity="0.8" transform="rotate(${rot} ${fmt(cx)} ${fmt(cy)})"/>`
      );
    })
    .join("");

  const blur = fmt(Math.min(W, H) * 0.12);
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid slice" role="img" aria-hidden="true">` +
    `<defs>` +
    `<linearGradient id="w" x1="${x1}%" y1="${y1}%" x2="${x2}%" y2="${y2}%">` +
    `<stop offset="0" stop-color="${washFrom}"/><stop offset="1" stop-color="${washTo}"/>` +
    `</linearGradient>` +
    `<filter id="b" x="-30%" y="-30%" width="160%" height="160%"><feGaussianBlur stdDeviation="${blur}"/></filter>` +
    `</defs>` +
    `<rect width="${W}" height="${H}" fill="url(#w)"/>` +
    `<g filter="url(#b)">${ellipses}</g>` +
    `</svg>`
  );
}

/** The art as a `data:image/svg+xml` URI for an `<img src>` / CSS `url()`. */
export function placeholderArt(
  palette: PlaceholderPalette,
  seed: string,
  options?: PlaceholderArtOptions,
): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(placeholderArtSvg(palette, seed, options))}`;
}
